import React, { useState, useEffect, useRef } from "react";
import "./CreativeDesignSection3.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons";
import Branding from "../../../assets/Video Icon.png";
import GraphicDesign from "../../../assets/graphicdesign.png";
import UIUX from "../../../assets/ux-creative.png";
import WebsiteDesign from "../../../assets/websitedesign.png";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CreativeDesignSection3 = () => {
  const { t } = useTranslation("global");
  const { lang } = useParams();
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderRef = useRef(null);
  const cardRefs = useRef([]);

  const services = [
    {
      id: 1,
      icon: Branding,
      title: t("creativeDesignSection3.services.branding.title"),
      description: t("creativeDesignSection3.services.branding.description"),
      link: "/Pricing/Creative",
    },
    {
      id: 2,
      icon: GraphicDesign,
      title: t("creativeDesignSection3.services.graphicDesign.title"),
      description: t(
        "creativeDesignSection3.services.graphicDesign.description"
      ),
      link: "/Pricing/Creative",
    },
    {
      id: 3,
      icon: UIUX,
      title: t("creativeDesignSection3.services.uiux.title"),
      description: t("creativeDesignSection3.services.uiux.description"),
      link: "/Pricing/Creative",
    },
    {
      id: 4,
      icon: WebsiteDesign,
      title: t("creativeDesignSection3.services.websiteDesign.title"),
      description: t(
        "creativeDesignSection3.services.websiteDesign.description"
      ),
      link: "/Pricing/Creative",
    },
  ];

  // Set up intersection observer to detect which card is in view
  useEffect(() => {
    // Only set up observer on mobile
    if (window.innerWidth >= 768) return;

    let isMounted = true; // prevent state update on unmounted component

    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.6, // Card is considered in view when 60% visible
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = cardRefs.current.findIndex(
            (ref) => ref === entry.target
          );
          if (index !== -1 && isMounted) {
            setActiveSlide(index); // updated with isMounted check
          }
        }
      });
    }, options);

    // Observe all card elements
    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      isMounted = false;
      observer.disconnect(); // disconnect observer on cleanup
    };
  }, []);

  // Scroll to a specific card when indicator is clicked
  const scrollToCard = (index) => {
    if (cardRefs.current[index]) {
      cardRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
      setActiveSlide(index);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section
      className="innovation-section"
      aria-labelledby="innovation-heading"
    >
      {/* Left Side - Text Content */}
      <div className="innovation-text">
        <p className="subheading">{t("creativeDesignSection3.subheading")}</p>
        <h2 id="innovation-heading" className="section-heading">
          {t("creativeDesignSection3.heading.part1")}{" "}
          <span className="highlight">
            {t("creativeDesignSection3.heading.highlight")}
          </span>
        </h2>
        <hr className="divide-creativesection3" />
        <p className="description-creativesection3">
          {t("creativeDesignSection3.description")}
        </p>
        <Link
          to={`/${lang}/Pricing/Creative`}
          onClick={scrollToTop}
          className="cta"
          aria-label="View creative design pricing options"
        >
          {t("creativeDesignSection3.ctaButton")}
          <FontAwesomeIcon icon={faAngleRight} aria-hidden="true" />
        </Link>
      </div>

      {/* Right Side - Service Cards */}
      <div className="service-cards-container">
        <div className="service-cards-creativedesign" ref={sliderRef}>
          {services.map((service, index) => (
            <Link
              to={`/${lang}${service.link}`}
              onClick={scrollToTop}
              key={service.id}
              className="service-card-link"
              aria-label={`Learn more about ${service.title}`}
              style={{ textDecoration: "none" }}
            >
              <article
                className="service-card-creativedesign"
                ref={(el) => (cardRefs.current[index] = el)}
              >
                <img
                  src={service.icon || "/placeholder.svg"}
                  className="service-icon"
                  alt={t("creativeDesignSection3.services.iconAlt", {
                    title: service.title,
                  })}
                  loading="lazy"
                  width="80"
                  height="80"
                  title={service.title}
                />
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </article>
            </Link>
          ))}
        </div>

        {/* Slider indicators for mobile */}
        <div
          className="slider-indicators"
          role="tablist"
          aria-label="Service navigation"
        >
          {services.map((service, index) => (
            <div
              key={index}
              className={`slider-dot ${activeSlide === index ? "active" : ""}`}
              onClick={() => scrollToCard(index)}
              role="tab"
              aria-selected={activeSlide === index}
              aria-label={t("creativeDesignSection3.sliderAriaLabel", {
                index: index + 1,
              })}
              aria-controls={`service-${service.id}`}
              tabIndex={0}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CreativeDesignSection3;
